/**
 * CR318 + CR322: Production Health Drawer
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Globe,
  Wifi,
  Lock,
  Database,
  Clock,
  Rocket,
  Eye,
  Zap,
  Settings,
} from "lucide-react";
import { useProductionHealth, type HealthStatus, type HealthLabel } from "@/hooks/useProductionHealth";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

function StatusIcon({ status }: { status: HealthStatus }) {
  switch (status) {
    case "healthy": return <CheckCircle2 className="h-6 w-6 text-green-500" />;
    case "degraded": return <AlertTriangle className="h-6 w-6 text-amber-500" />;
    case "outage": return <XCircle className="h-6 w-6 text-red-500" />;
  }
}

function LabelIcon({ label }: { label: HealthLabel }) {
  switch (label) {
    case "No Snapshot": return <Globe className="h-4 w-4" />;
    case "Edge Down": return <Wifi className="h-4 w-4" />;
    case "Auth": return <Lock className="h-4 w-4" />;
    case "DB": return <Database className="h-4 w-4" />;
    default: return <AlertTriangle className="h-4 w-4" />;
  }
}

export function ProductionHealthDrawer() {
  const navigate = useNavigate();
  const health = useProductionHealth();
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const {
    classification, checks, updatedAt, isRunning, drawerOpen,
    edgeProbe, supabaseConfig, edgeDebugSummary,
    closeDrawer, runTriage, copyDiagnostics,
  } = health;

  const handleCopy = async () => {
    const success = await copyDiagnostics();
    if (success) {
      setCopied(true);
      toast.success("Diagnostics copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Failed to copy diagnostics");
    }
  };

  const handleOpenLivePreview = () => {
    closeDrawer();
    navigate("/admin/live-preview?openHealth=1");
  };

  const getPrimaryCTA = () => {
    switch (classification.label) {
      case "No Snapshot":
        return { label: "Publish Snapshot", icon: Rocket, action: () => { closeDrawer(); navigate("/admin/batch-runs"); } };
      case "Edge Down":
        return { label: "Run Triage", icon: RefreshCw, action: runTriage };
      case "Auth":
        return { label: "Refresh Session", icon: Lock, action: () => window.location.reload() };
      case "DB":
        return { label: "View Logs", icon: Database, action: () => { closeDrawer(); navigate("/admin/live-preview?openHealth=1"); } };
      default:
        return { label: "Run Triage", icon: RefreshCw, action: runTriage };
    }
  };

  const cta = getPrimaryCTA();
  const CTAIcon = cta.icon;
  const edge = checks.edge;

  const lastCheckedText = updatedAt
    ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
    : "Never";

  return (
    <Drawer open={drawerOpen} onOpenChange={(open) => !open && closeDrawer()}>
      <DrawerContent className="max-w-md ml-auto h-full">
        <DrawerHeader className="border-b">
          <div className="flex items-center gap-3">
            <StatusIcon status={classification.status} />
            <div>
              <DrawerTitle className="text-lg">Production Health</DrawerTitle>
              <DrawerDescription className="flex items-center gap-2 mt-1">
                <Clock className="h-3 w-3" />
                Last checked {lastCheckedText}
              </DrawerDescription>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Headline Issue */}
          {classification.label && (
            <div className={`p-4 rounded-lg border-2 ${classification.status === "outage" ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
              <div className="flex items-center gap-2 mb-2">
                <LabelIcon label={classification.label} />
                <span className="text-lg font-bold">{classification.label}</span>
              </div>
              <p className="text-sm">{classification.reason}</p>
              {classification.secondaryReason && (
                <p className="text-sm text-muted-foreground mt-2">{classification.secondaryReason}</p>
              )}
            </div>
          )}

          {/* Healthy State */}
          {classification.status === "healthy" && !classification.label && (
            <div className="p-4 rounded-lg border-2 bg-green-500/10 border-green-500/30">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-lg font-bold">All Systems Operational</span>
              </div>
              <p className="text-sm">{classification.reason}</p>
            </div>
          )}

          {/* Status Pills */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={checks.snapshot?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : checks.snapshot?.status === "MISSING" ? "bg-red-500/10 text-red-700 border-red-500/30" : "bg-amber-500/10 text-amber-700 border-amber-500/30"}>
              <Globe className="h-3 w-3 mr-1" />
              Snapshot: {checks.snapshot?.status || "?"}
            </Badge>
            <Badge variant="outline" className={edge?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-red-500/10 text-red-700 border-red-500/30"}>
              <Wifi className="h-3 w-3 mr-1" />
              Edge: {edge?.status || "?"}
            </Badge>
            <Badge variant="outline" className={checks.auth?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-amber-500/10 text-amber-700 border-amber-500/30"}>
              <Lock className="h-3 w-3 mr-1" />
              Auth: {checks.auth?.status || "?"}
            </Badge>
            <Badge variant="outline" className={checks.db?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-red-500/10 text-red-700 border-red-500/30"}>
              <Database className="h-3 w-3 mr-1" />
              DB: {checks.db?.status || "?"}
            </Badge>
            {edgeProbe && (
              <Badge variant="outline" className={edgeProbe.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : edgeProbe.status === "ERROR" ? "bg-red-500/10 text-red-700 border-red-500/30" : "bg-muted text-muted-foreground"}>
                <Zap className="h-3 w-3 mr-1" />
                Probe: {edgeProbe.status}
              </Badge>
            )}
          </div>

          {/* CR322: Debug Summary */}
          {edgeDebugSummary && (
            <div className="p-2 bg-muted/50 rounded border text-[11px] font-mono text-muted-foreground break-all">
              {edgeDebugSummary}
            </div>
          )}

          <Separator />

          {/* Primary CTA */}
          <Button onClick={cta.action} disabled={isRunning} className="w-full">
            <CTAIcon className={`h-4 w-4 mr-2 ${isRunning ? "animate-spin" : ""}`} />
            {isRunning ? "Running..." : cta.label}
          </Button>

          {/* Secondary Actions */}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={handleOpenLivePreview}>
              <Eye className="h-4 w-4 mr-2" />
              Live Preview Details
            </Button>
            <Button variant="outline" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          <Separator />

          {/* Expandable Details */}
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="text-sm font-medium">Technical Details</span>
                {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 mt-2">
              {/* Snapshot */}
              <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground">Snapshot</div>
                <div>ID: {checks.snapshot?.snapshotId?.slice(0, 20) || "null"}...</div>
                <div>Published: {checks.snapshot?.publishedAt || "null"}</div>
                <div>Deals: {checks.snapshot?.dealCount ?? "null"}</div>
              </div>

              {/* Edge (CR322 expanded) */}
              <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground">Edge Function</div>
                <div>Function: {edge?.resolvedFunctionName || "N/A"}</div>
                <div className="break-all">URL: {edge?.resolvedEdgeUrl || "N/A"}</div>
                <div>Method: {edge?.requestMethod || "N/A"}</div>
                <div>HTTP: {edge?.httpStatus || "N/A"}</div>
                <div>Latency: {edge?.latencyMs ?? "N/A"}ms</div>
                <div>Auth Header: {edge?.requestHeadersPresent?.authorization ? "Yes" : "No"}</div>
                <div>Invoke Attempted: {edge?.invokeAttempted ? "Yes" : "No"}</div>
                {edge?.fetchError && (
                  <div className="text-red-500 mt-1">{edge.fetchError.name}: {edge.fetchError.message}</div>
                )}
                {edge?.payloadSnippet && <div>Payload: {edge.payloadSnippet}</div>}
              </div>

              {/* Edge Probe (CR322) */}
              {edgeProbe && (
                <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                  <div className="font-semibold text-foreground">Edge Probe</div>
                  <div>Function: {edgeProbe.functionName}</div>
                  <div>Status: {edgeProbe.status} | HTTP: {edgeProbe.httpStatus ?? "N/A"}</div>
                  <div>Latency: {edgeProbe.latencyMs ?? "N/A"}ms</div>
                  {edgeProbe.error && <div className="text-red-500">Error: {edgeProbe.error}</div>}
                </div>
              )}

              {/* Supabase Config (CR322) */}
              {supabaseConfig && (
                <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                  <div className="font-semibold text-foreground">Client Config</div>
                  <div className="break-all">URL: {supabaseConfig.url || "null"}</div>
                  <div>Anon Key: {supabaseConfig.anonKeyPresent ? "Present" : "Missing"}</div>
                  <div>Project Ref: {supabaseConfig.projectRefDerived || "null"}</div>
                  <div>Init Source: {supabaseConfig.clientInitSource || "null"}</div>
                </div>
              )}

              {/* Auth */}
              <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground">Authentication</div>
                <div>Session: {checks.auth?.hasSession ? "Yes" : "No"}</div>
                <div>Token: {checks.auth?.hasToken ? "Present" : "Missing"}</div>
              </div>

              {/* DB */}
              <div className="p-3 bg-muted/50 rounded-lg text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground">Database</div>
                <div>Latency: {checks.db?.latencyMs ?? "N/A"}ms</div>
                {checks.db?.message && <div className="text-red-500">Error: {checks.db.message}</div>}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DrawerFooter className="border-t">
          <DrawerClose asChild>
            <Button variant="outline">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
